import React from 'react';
import { PincodeDetails } from '../../types/pincodeTypes';
import Badge from '../bootstrap/Badge';
import Button from '../bootstrap/Button';
import Card, { CardBody } from '../bootstrap/Card';

interface SelectedPincodesProps {
  pincodes: PincodeDetails[];
  onRemove: (pincodeId: number) => void;
  showTitle?: boolean;
  showStats?: boolean;
  maxDisplay?: number;
  className?: string;
}

const SelectedPincodes: React.FC<SelectedPincodesProps> = ({
  pincodes,
  onRemove,
  showTitle = true,
  showStats = true,
  maxDisplay,
  className = ''
}) => {
  if (pincodes.length === 0) {
    return null;
  }

  const displayPincodes = maxDisplay ? pincodes.slice(0, maxDisplay) : pincodes;
  const hiddenCount = maxDisplay && pincodes.length > maxDisplay ? pincodes.length - maxDisplay : 0;

  // Group pincodes by state for better organization
  const pincodesByState = pincodes.reduce((acc, pincode) => {
    const state = pincode.stateName;
    if (!acc[state]) {
      acc[state] = [];
    }
    acc[state].push(pincode);
    return acc;
  }, {} as Record<string, PincodeDetails[]>);

  const stateCount = Object.keys(pincodesByState).length;

  return (
    <Card className={`pincode-selection-card ${className}`}>
      <CardBody>
        {showTitle && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 text-primary">
              <i className="fas fa-map-marker-alt me-2"></i>
              Selected Pincodes
            </h6>
            {showStats && (
              <div className="d-flex gap-2">
                <Badge color="info" isLight>
                  {pincodes.length} pincode{pincodes.length !== 1 ? 's' : ''}
                </Badge>
                <Badge color="secondary" isLight>
                  {stateCount} state{stateCount !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </div>
        )}

        <div className="pincode-tags-container">
          {displayPincodes.map((pincode) => (
            <div
              key={pincode.pincodeId}
              className="pincode-tag d-inline-flex align-items-center bg-light border rounded-pill px-3 py-2 me-2 mb-2"
              style={{ fontSize: '0.875rem' }}
            >
              <div className="pincode-info">
                <div className="fw-bold text-primary">{pincode.pincode}</div>
                <div className="text-dark small">{pincode.areaName}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {pincode.cityName ? `${pincode.cityName}, ` : ''}{pincode.districtName}, {pincode.stateName}
                </div>
              </div>
              <Button
                color="danger"
                size="sm"
                isOutline
                className="ms-3 btn-sm-custom"
                onClick={() => onRemove(pincode.pincodeId)}
                style={{ 
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  lineHeight: 1
                }}
                title={`Remove ${pincode.pincode} - ${pincode.areaName}`}
              >
                ×
              </Button>
            </div>
          ))}

          {hiddenCount > 0 && (
            <div className="d-inline-flex align-items-center bg-secondary text-white rounded-pill px-3 py-2 me-2 mb-2">
              <small>+{hiddenCount} more</small>
            </div>
          )}
        </div>

        {showStats && (
          <div className="mt-3 pt-3 border-top">
            <div className="row text-center">
              <div className="col-4">
                <div className="fw-bold text-primary">{pincodes.length}</div>
                <small className="text-muted">Pincodes</small>
              </div>
              <div className="col-4">
                <div className="fw-bold text-info">{stateCount}</div>
                <small className="text-muted">States</small>
              </div>
              <div className="col-4">
                <div className="fw-bold text-success">
                  {Object.values(pincodesByState).reduce((acc, statePincodes) => {
                    const cities = new Set(
                      statePincodes
                        .map(p => p.cityName || p.districtName)
                        .filter(Boolean)
                    );
                    return acc + cities.size;
                  }, 0)}
                </div>
                <small className="text-muted">Cities/Districts</small>
              </div>
            </div>
          </div>
        )}

        {pincodes.length > 5 && (
          <div className="mt-2">
            <Button
              color="secondary"
              isOutline
              size="sm"
              onClick={() => {
                // Clear all pincodes
                pincodes.forEach(pincode => onRemove(pincode.pincodeId));
              }}
            >
              Clear All
            </Button>
          </div>
        )}
      </CardBody>

      <style>{`
        .pincode-tag {
          transition: all 0.2s ease-in-out;
        }
        
        .pincode-tag:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .btn-sm-custom {
          min-width: auto;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .pincode-selection-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border: 1px solid #e9ecef;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
      `}</style>
    </Card>
  );
};

export default SelectedPincodes;
